import { useRef, useEffect, useCallback } from 'react';

type ThrottleOptions = {
  leading?: boolean;
  trailing?: boolean;
};

/**
 * To Avoid Frequent Calls
 *
 * @param callback - function to throttle
 * @param delay - minimum time between calls in milliseconds
 * @param options - additional throttle settings
 * @returns - throttled version of the function
 */
export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 2000,
  options: ThrottleOptions = { leading: true, trailing: false }
): T {
  const { leading = true, trailing = false } = options; // leading - immediate call, trailing - delayed call

  // Save callback
  const callbackRef = useRef<T>(callback);
  // Flag, showing if the function can be called
  const isThrottledRef = useRef<boolean>(false);
  // Arguments of the last call for trailing
  const lastArgsRef = useRef<any[] | null>(null);
  // Timer for trailing call
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update callback when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Clean up timer when it unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    ((...args: any[]) => {
      // If already throttled, store args and exit
      if (isThrottledRef.current) {
        if (trailing) {
          lastArgsRef.current = args;
        }
        return;
      }

      // Set throttled state immediately
      isThrottledRef.current = true;

      // Leading call (immediate)
      if (leading) {
        callbackRef.current(...args);
      } else if (trailing) {
        // If not leading, store args for trailing call
        lastArgsRef.current = args;
      }

      // Set timer for the end of the throttle period
      timeoutRef.current = setTimeout(() => {
        const lastArgs = lastArgsRef.current;

        // Reset throttle state and last args
        isThrottledRef.current = false;
        lastArgsRef.current = null;

        // Trailing call (delayed) - only if we have stored args
        if (trailing && lastArgs) {
          callbackRef.current(...lastArgs);
        }
      }, delay);
    }) as T,
    [delay, leading, trailing]
  );
}
